import {
  FAQ_SECTIONS,
  GUIDE_TYPES,
  parseFrontMatter,
  type FaqSection,
  type FrontMatter,
  type GuideType,
} from './front-matter.ts';
// Con l'estensione: anche vite.config.ts carica questo modulo (avvisi della Guida nella build).
import { HELP_SCREENS, type HelpScreen } from './screens.ts';

/** Radice delle rotte della Guida nella web app. */
export const GUIDE_BASE = '/guida';

/** Cartella di ogni tipo sotto `docs/manuale/operatori/`; è anche l'ancora del tipo nell'indice. */
export const TYPE_DIRS: Record<GuideType, string> = {
  'primi-passi': 'primi-passi',
  procedura: 'procedure',
  riferimento: 'riferimento',
  concetto: 'concetti',
  faq: 'faq',
};

/** Nome dei tipi nell'indice. */
export const TYPE_LABELS: Record<GuideType, string> = {
  'primi-passi': 'Primi passi',
  procedura: 'Procedure',
  riferimento: 'Riferimento',
  concetto: 'Concetti',
  faq: 'FAQ',
};

/** Nome del tipo accanto a una singola pagina (risultati della ricerca, intestazione). */
export const TYPE_SINGULAR_LABELS: Record<GuideType, string> = {
  'primi-passi': 'Primi passi',
  procedura: 'Procedura',
  riferimento: 'Riferimento',
  concetto: 'Concetto',
  faq: 'FAQ',
};

/** Nome delle sezioni delle FAQ. */
export const FAQ_SECTION_LABELS: Record<FaqSection, string> = {
  'domande-frequenti': 'Domande frequenti',
  'risoluzione-problemi': 'Risoluzione dei problemi',
};

/** Una pagina della Guida: front-matter validato, posizione nel manuale e testo Markdown. */
export interface GuidePage extends FrontMatter {
  /** Percorso relativo a `docs/manuale/operatori/`, es. `procedure/assegnare-un-ticket.md`. */
  path: string;
  /** Nome del file senza `.md`: per le voci FAQ è l'ancora nella pagina della sezione. */
  slug: string;
  /** Il Markdown dopo il front-matter. */
  body: string;
}

/** Un file del manuale che la Guida non può mostrare, o che contraddice un'altra pagina, con il motivo. */
export interface GuideProblem {
  path: string;
  message: string;
}

/** Le pagine della Guida in ordine di indice, più i problemi trovati (file scartati, conflitti). */
export interface GuideCatalog {
  pages: GuidePage[];
  problems: GuideProblem[];
}

/** Le pagine per percorso: il contesto in cui si risolvono i link. */
export type PagesByPath = ReadonlyMap<string, GuidePage>;

/** Indicizza le pagine per percorso relativo al manuale. */
export function pagesByPath(pages: readonly GuidePage[]): PagesByPath {
  return new Map(pages.map((page) => [page.path, page]));
}

const KEBAB = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const collator = new Intl.Collator('it', { sensitivity: 'base', ignorePunctuation: true });

/** La cartella in cui deve stare una pagina, dal suo tipo (e sezione, per le FAQ). */
export function expectedDir(data: Pick<FrontMatter, 'tipo' | 'sezione'>): string {
  return data.tipo === 'faq' ? `faq/${data.sezione ?? ''}` : TYPE_DIRS[data.tipo];
}

/** Ordine dell'indice: per tipo, per sezione delle FAQ, poi per titolo. */
export function comparePages(a: GuidePage, b: GuidePage): number {
  return (
    GUIDE_TYPES.indexOf(a.tipo) - GUIDE_TYPES.indexOf(b.tipo) ||
    FAQ_SECTIONS.indexOf(a.sezione ?? 'domande-frequenti') - FAQ_SECTIONS.indexOf(b.sezione ?? 'domande-frequenti') ||
    collator.compare(a.titolo, b.titolo) ||
    a.path.localeCompare(b.path)
  );
}

/**
 * Costruisce il catalogo dai file del manuale (`percorso relativo → Markdown`). Un file con il
 * front-matter non valido, fuori dalla cartella del suo tipo o con un nome non in kebab-case resta
 * fuori, con il motivo in `problems`; lo stesso per due pagine che si dichiarano `principale` della
 * stessa schermata (restano entrambe nella Guida). Il test del contenuto fa fallire ogni problema.
 */
export function buildCatalog(files: Readonly<Record<string, string>>): GuideCatalog {
  const pages: GuidePage[] = [];
  const problems: GuideProblem[] = [];
  for (const [path, markdown] of Object.entries(files)) {
    const parsed = parseFrontMatter(markdown);
    if (!parsed.ok) {
      problems.push(...parsed.errors.map((message) => ({ path, message })));
      continue;
    }
    const slash = path.lastIndexOf('/');
    const dir = path.slice(0, Math.max(slash, 0));
    const slug = path.slice(slash + 1).replace(/\.md$/, '');
    if (dir !== expectedDir(parsed.data)) {
      problems.push({ path, message: `una pagina di tipo «${parsed.data.tipo}» va in «${expectedDir(parsed.data)}/»` });
      continue;
    }
    if (!KEBAB.test(slug)) {
      problems.push({ path, message: 'il nome del file va scritto in kebab-case, es. assegnare-un-ticket.md' });
      continue;
    }
    pages.push({ ...parsed.data, path, slug, body: parsed.body });
  }
  pages.sort(comparePages);
  return { pages, problems: [...problems, ...mainConflicts(pages)] };
}

/** Le schermate dichiarate `principale` da più di una pagina: il «?» non saprebbe quale aprire. */
function mainConflicts(pages: readonly GuidePage[]): GuideProblem[] {
  const byScreen = new Map<string, string[]>();
  for (const page of pages) {
    for (const screen of page.principale) byScreen.set(screen, [...(byScreen.get(screen) ?? []), page.path]);
  }
  return [...byScreen].flatMap(([screen, paths]) =>
    paths.length < 2
      ? []
      : paths.map((path) => ({
          path,
          message: `anche ${paths.filter((other) => other !== path).join(', ')} si dichiara «principale» per ${screen}`,
        }))
  );
}

/** URL della pagina di una sezione delle FAQ, che raccoglie tutte le sue voci. */
export function faqSectionUrl(section: FaqSection): string {
  return `${GUIDE_BASE}/faq/${section}`;
}

/** URL del tipo nell'indice della Guida. */
export function typeUrl(type: GuideType): string {
  return `${GUIDE_BASE}#${TYPE_DIRS[type]}`;
}

/**
 * Ancora di un titolo interno a una pagina. Nelle pagine delle FAQ le voci stanno una sotto
 * l'altra: i titoli interni di una voce prendono il nome della voce come prefisso.
 */
export function headingAnchor(page: GuidePage, heading: string): string {
  return page.tipo === 'faq' ? `${page.slug}--${heading}` : heading;
}

/**
 * URL di una pagina, o di un suo punto: una voce FAQ si apre nella pagina della sua sezione, sulla
 * sua ancora.
 */
export function pageUrl(page: GuidePage, heading?: string): string {
  if (page.tipo === 'faq') {
    return `${faqSectionUrl(page.sezione ?? 'domande-frequenti')}#${heading ? headingAnchor(page, heading) : page.slug}`;
  }
  const base = `${GUIDE_BASE}/${page.path.replace(/\.md$/, '')}`;
  return heading ? `${base}#${heading}` : base;
}

/** Cosa mostra il «?» di una schermata. */
export interface ScreenHelp {
  /** La pagina aperta dal «?»: quella `principale`, altrimenti la prima procedura o la prima pagina che non è una FAQ. */
  main?: GuidePage;
  /** Le altre pagine della schermata che non sono FAQ. */
  seeAlso: GuidePage[];
  /** Le voci FAQ della schermata: prima le Domande frequenti, poi la Risoluzione dei problemi. */
  faq: GuidePage[];
}

/**
 * Le pagine del «?» di una schermata (convenzioni, campi `schermate` e `principale`). Apre la pagina
 * che si dichiara `principale` per la schermata; altrimenti la prima procedura nell'ordine
 * dell'indice (per titolo), e senza procedure la prima pagina di un altro tipo. Due dichiarazioni
 * per la stessa schermata sono un errore del contenuto: qui vince la prima nell'ordine dell'indice.
 */
export function helpFor(screen: HelpScreen, pages: readonly GuidePage[]): ScreenHelp {
  const forScreen = pages.filter((page) => page.schermate.includes(screen));
  const guides = forScreen.filter((page) => page.tipo !== 'faq');
  const declared = guides.find((page) => page.principale.includes(screen));
  const main = declared ?? guides.find((page) => page.tipo === 'procedura') ?? guides[0];
  return {
    ...(main ? { main } : {}),
    seeAlso: guides.filter((page) => page !== main),
    faq: forScreen.filter((page) => page.tipo === 'faq'),
  };
}

/** Una schermata con il «?» a cui manca qualcosa nella Guida. */
export interface HelpGap {
  screen: HelpScreen;
  /** `nessuna-pagina`: il «?» non compare; `nessuna-procedura`: il «?» mostra solo altre pagine o FAQ. */
  kind: 'nessuna-pagina' | 'nessuna-procedura';
  message: string;
}

/**
 * Le mancanze della Guida rispetto alle schermate con il «?». Sono avvisi, non errori (PRD-006
 * US-1): li riportano la build della web app e i test, come «todo».
 */
export function helpGaps(pages: readonly GuidePage[]): HelpGap[] {
  return (Object.keys(HELP_SCREENS) as HelpScreen[]).flatMap((screen): HelpGap[] => {
    const name = `${HELP_SCREENS[screen]} (${screen})`;
    const help = helpFor(screen, pages);
    if (!help.main && help.faq.length === 0) {
      return [
        {
          screen,
          kind: 'nessuna-pagina',
          message: `${name}: nessuna pagina indica questa schermata, il «?» non compare`,
        },
      ];
    }
    if (![help.main, ...help.seeAlso].some((page) => page?.tipo === 'procedura')) {
      const shown = help.main ? 'altre pagine' : 'le voci FAQ';
      return [
        { screen, kind: 'nessuna-procedura', message: `${name}: nessuna procedura, il «?» mostra solo ${shown}` },
      ];
    }
    return [];
  });
}
